import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../utils/axios";
import { formatCurrency } from "../helpers";

interface DepositItem {
  transaction_id: number;
  uid: string;
  name: string;
  amount: number;
  status: string;
  transaction_date: string;
  payment_type: "crypto" | "bank" | "online";
  paymentmethod: string;
  transaction_type?: string;
  utr_no: string | null;
  txhash: string | null;
  wallet_address: string | null;
  transactionid: string | null;
  receipt?: string;
  totalbonuspercentage?: number;
}

const ManualDeposit = () => {
  const [tab, setTab] = useState<"crypto" | "bank" | "online">("online");
  const [data, setData] = useState<DepositItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, [tab, page, search]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(
        `/getManualDepositData?page=${page}&limit=10`,
        {
          params: {
            payment_type: tab,
            transactionType: tab,
            search: search.trim() || undefined,
          },
          headers: { Authorization: token },
        }
      );

      setData(res?.data?.data || []);
      setTotalPages(res?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching deposit data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (d: DepositItem, isApproved: boolean) => {
    const actionText = isApproved ? "Approve" : "Reject";
    const result = await Swal.fire({
      title: `${actionText} Deposit?`,
      html: `Are you sure you want to <b>${actionText.toLowerCase()}</b> deposit of <b style="color:#059669">${formatCurrency(d.amount)}</b> for User <b>${d.name || d.uid}</b>?`,
      icon: isApproved ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: isApproved ? "#16a34a" : "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Yes, ${actionText}!`,
    });

    if (!result.isConfirmed) return;

    try {
      await api.post(
        "/approveRejectManualDeposit",
        {
          trx: d,
          isApproved,
        },
        { headers: { Authorization: token } }
      );

      Swal.fire({
        title: isApproved ? "Approved!" : "Rejected!",
        text: `Deposit has been ${isApproved ? "approved" : "rejected"} successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchData();
    } catch (err: any) {
      console.error("Error approving/rejecting deposit:", err);
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const filteredData = data.filter((d) => {
    if (!search.trim()) return true;
    const keyword = search.toLowerCase();
    return (
      String(d.uid || "").toLowerCase().includes(keyword) ||
      String(d.name || "").toLowerCase().includes(keyword) ||
      String(d.utr_no || "").toLowerCase().includes(keyword) ||
      String(d.txhash || "").toLowerCase().includes(keyword) ||
      String(d.wallet_address || "").toLowerCase().includes(keyword)
    );
  });

  return (
    <div className="space-y-6">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Deposit Management</h1>
        <p className="text-sm text-gray-500">
          Manage Online, Crypto & Bank deposit approvals
        </p>
      </div>

      {/* ================= SEARCH & TABS ================= */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* ================= TABS ================= */}
        <div className="flex gap-2">
          {(["online", "crypto", "bank"] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                tab === t
                  ? "bg-gray-900 text-white shadow"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t === "online"
                ? "Online Deposits"
                : t === "crypto"
                ? "Crypto Deposits"
                : "Bank Deposits"}
            </button>
          ))}
        </div>

        {/* ================= SEARCH ================= */}
        <div className="w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search by UID, UTR, TxHash, Name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-80 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </div>

      {/* ================= TABLE ================= */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600 font-semibold border-b">
            <tr>
              <th className="px-4 py-3 text-center">#</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Bonus</th>

              {tab === "online" ? (
                <>
                  <th className="px-4 py-3">Payment Method</th>
                  <th className="px-4 py-3">UTR / Order No</th>
                </>
              ) : tab === "crypto" ? (
                <>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">TxHash / Order</th>
                  <th className="px-4 py-3">Wallet Address</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3">Payment Method</th>
                  <th className="px-4 py-3">UTR / Ref</th>
                </>
              )}

              <th className="px-4 py-3 text-center">Receipt</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-gray-400">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mb-2" />
                  <div>Loading records...</div>
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-gray-500">
                  No records found in {tab} deposits
                </td>
              </tr>
            ) : (
              filteredData.map((d, i) => {
                const bonusAmount = (d.amount * (d.totalbonuspercentage ?? 0)) / 100;
                const isOnlineGateway = d.payment_type === "online" && !d.txhash;

                return (
                  <tr
                    key={d.transaction_id || i}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-center text-gray-500 font-mono text-xs">
                      {(page - 1) * 10 + i + 1}
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{d.name || d.uid}</div>
                      {d.name && d.name !== d.uid && (
                        <div className="text-xs text-gray-500 font-mono">{d.uid}</div>
                      )}
                    </td>

                    {/* AMOUNT */}
                    <td className="px-4 py-3 font-semibold text-gray-900">
                      {formatCurrency(d.amount)}
                    </td>

                    {/* BONUS */}
                    <td className="px-4 py-3 text-gray-600">
                      {bonusAmount > 0 ? (
                        <span className="text-emerald-600 font-medium">
                          +{formatCurrency(bonusAmount)}
                        </span>
                      ) : (
                        "$0.00"
                      )}
                    </td>

                    {/* PAYMENT METHOD / DETAILS */}
                    {tab === "online" ? (
                      <>
                        <td className="px-4 py-3">
                          <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 font-medium text-xs">
                            {d.paymentmethod || "Online"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-xs font-mono text-gray-600 select-all max-w-[200px] truncate" title={d.utr_no || ""}>
                          {d.utr_no || "-"}
                        </td>
                      </>
                    ) : tab === "crypto" ? (
                      <>
                        <td className="px-4 py-3">
                          <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold text-xs">
                            {d.paymentmethod === "ccpayment" ? "Auto USDT" : (d.paymentmethod || "USDT")}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-xs font-mono text-gray-600 max-w-[160px] truncate select-all" title={d.txhash || d.utr_no || ""}>
                          {d.txhash || d.utr_no || "-"}
                        </td>

                        <td className="px-4 py-3 text-xs font-mono text-gray-600 max-w-[160px] truncate select-all" title={d.wallet_address || ""}>
                          {d.wallet_address || "-"}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 font-medium text-xs">
                            {d.paymentmethod || "Bank"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-xs font-mono text-gray-600 select-all">
                          {d.utr_no || "-"}
                        </td>
                      </>
                    )}

                    {/* RECEIPT */}
                    <td className="px-4 py-3 text-center">
                      {d.receipt ? (
                        <a
                          href={`${process.env.REACT_APP_API_URL}/${d.receipt}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* STATUS */}
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                          d.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : d.status === "accepted" || d.status === "completed" || d.status === "success"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {d.status}
                      </span>
                    </td>

                    {/* DATE */}
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(d.transaction_date).toLocaleString()}
                    </td>

                    {/* ACTION */}
                    <td className="px-4 py-3 text-center">
                      {d.status === "pending" ? (
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => handleAction(d, true)}
                            className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition cursor-pointer"
                          >
                            Accept
                          </button>

                          <button
                            onClick={() => handleAction(d, false)}
                            className="px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium transition cursor-pointer"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {isOnlineGateway ? "Online Deposit" : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* ================= PAGINATION ================= */}
        <div className="flex justify-between items-center px-4 py-3 border-t bg-gray-50">
          <span className="text-xs text-gray-500">
            Page {page} of {totalPages}
          </span>

          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 text-xs border rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Previous
            </button>

            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 text-xs border rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualDeposit;